/**
 * Gamification API
 *
 * RTK Query endpoints for authority levels, badges, and gamification features.
 */

import { api } from '../api';

// ============================================
// TYPES
// ============================================

export type AuthorityLevelKey = 'novice' | 'bronze' | 'silver' | 'gold' | 'platinum';

export interface AuthorityLevelDefinition {
  key: AuthorityLevelKey;
  label: string;
  minOverallScore: number;
  minSessions: number;
  minDomainsPassed?: number;
  color: string;
  icon?: string;
}

export interface UserAuthoritySnapshot {
  userId: string;
  currentLevel: AuthorityLevelKey;
  currentLevelLabel: string;
  /** null = nothing measured yet. */
  overallScoreAvg: number | null;
  sessionsCount: number;
  domainsPassed: number;
  totalDomainsAttempted: number;
  nextLevel: AuthorityLevelDefinition | null;
  progressToNextLevel: {
    scoreProgress: number;
    sessionsProgress: number;
    domainsProgress: number;
    overallProgress: number;
  };
  lastUpdatedAt: string;
}

/**
 * Badge keys — one vocabulary shared by the authority badges and the reputation
 * badges (backend types/gamification.ts). A persisted key outside the catalog is
 * still returned (verbatim) so nothing earned is hidden; hence `string`.
 */
export type BadgeKey = string;

export type BadgeCategory =
  | 'milestone'
  | 'mastery'
  | 'performance'
  | 'engagement'
  | 'marketplace'
  | 'verification'
  | 'specialty'
  | 'improvement'
  | 'other';

export type BadgeRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';

export interface EarnedBadge {
  key: BadgeKey;
  label: string;
  description: string;
  category: BadgeCategory;
  rarity: BadgeRarity;
  earnedAt: string;
}

export interface UserBadgeSnapshot {
  userId: string;
  earnedBadges: EarnedBadge[];
  totalBadges: number;
  recentBadge?: EarnedBadge;
  lastUpdatedAt: string;
}

export interface BadgeDefinition {
  key: BadgeKey;
  label: string;
  description: string;
  icon?: string;
  category: BadgeCategory;
  rarity: BadgeRarity;
}

export interface GamificationOverview {
  authority: UserAuthoritySnapshot;
  badges: EarnedBadge[];
  stats: {
    totalSessions: number;
    /** null = nothing measured yet. */
    avgScore: number | null;
    currentStreak: number;
    longestStreak: number;
    /**
     * XP actually earned (NPC level rewards). Never estimated. Omitted on
     * another user's overview when their NPC progress is not public.
     */
    totalXp?: number;
  };
}

/**
 * One row of GET /gamification/leaderboard. Only users who opted in (public
 * profile that shows authority scores, and discovery opt-in) with enough
 * measured sessions appear.
 */
export interface LeaderboardItem {
  rank: number;
  /** True only on the signed-in viewer's own row (the board publishes no user ids). */
  isCurrentUser: boolean;
  name: string;
  publicSlug: string | null;
  avgScore: number;
  sessionCount: number;
}

export interface LeaderboardResponse {
  items: LeaderboardItem[];
  total: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// ============================================
// API ENDPOINTS
// ============================================

export const gamificationApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Get my authority level snapshot
    getMyAuthority: builder.query<UserAuthoritySnapshot, void>({
      query: () => '/gamification/me/authority',
      transformResponse: (response: ApiResponse<UserAuthoritySnapshot>) => response.data,
      providesTags: ['User'],
    }),

    // Get my complete gamification overview
    getMyGamificationOverview: builder.query<GamificationOverview, void>({
      query: () => '/gamification/me/overview',
      transformResponse: (response: ApiResponse<GamificationOverview>) => response.data,
      providesTags: ['User'],
    }),

    // Get authority leaderboard
    getLeaderboard: builder.query<LeaderboardResponse, { limit?: number }>({
      query: ({ limit = 10 }) => `/gamification/leaderboard?limit=${limit}`,
      transformResponse: (response: ApiResponse<LeaderboardResponse>) => response.data,
      providesTags: ['User'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetMyAuthorityQuery,
  useGetMyGamificationOverviewQuery,
  useGetLeaderboardQuery,
} = gamificationApi;
